import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getCompanyIdForUser } from './firestoreService';

/**
 * Get user's/company's plugin data (Website Calculators, etc.).
 * When accountId is provided, prefer account-level plugin state so all team members share it.
 * @param {string} userId - The user's UID
 * @param {string|null} accountId - Optional account/company ID for shared data
 * @returns {Promise<Object>} Plugin data map
 */
export const getUserPluginData = async (userId, accountId = null) => {
  try {
    // Prefer company-wide plugin state when accountId is available
    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      if (accountDoc.exists()) {
        const accountData = accountDoc.data();
        const accountPlugins = accountData.userPlugins || {};
        if (Object.keys(accountPlugins).length > 0) {
          return accountPlugins;
        }
      }
    }

    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userPlugins || {};
    }

    return {};
  } catch (error) {
    console.error('Error getting user plugin data:', error);
    return {};
  }
};

/**
 * Save user's/company's plugin status.
 * When accountId is provided, also persist to the account so all team members share the status.
 * @param {string} userId - The user's UID
 * @param {string} pluginId - The plugin ID
 * @param {string} status - The plugin status ('off', 'requested', 'on')
 * @param {string|null} accountId - Optional account/company ID
 * @param {Object|null} [requestExtras] - When status is "requested", optional { apiKey: string } stored for CRM fulfillment
 * @returns {Promise<Object>} Result of the save operation
 */
const buildUserPluginEntry = (prev, status, requestExtras) => {
  const prevSafe = prev || {};
  if (status === 'off') {
    const { requestApiKey, ...rest } = prevSafe;
    return { ...rest, status, lastUpdated: serverTimestamp() };
  }
  const trimmed =
    requestExtras && requestExtras.apiKey != null
      ? String(requestExtras.apiKey).trim()
      : '';
  const base = { ...prevSafe, status, lastUpdated: serverTimestamp() };
  if (status === 'requested' && trimmed) {
    return { ...base, requestApiKey: trimmed };
  }
  return base;
};

export const saveUserPluginStatus = async (userId, pluginId, status, accountId = null, requestExtras = null) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    // Get user data for company/account info
    let userData = {};
    let company = 'Unknown Company';
    
    if (userDoc.exists()) {
      userData = userDoc.data();
      company = userData.company || userData.businessName || 'Unknown Company';
    }
    
    // Get plugin template data
    const pluginDoc = await getDoc(doc(db, 'pluginTemplates', pluginId));
    let pluginData = {};
    
    if (pluginDoc.exists()) {
      pluginData = pluginDoc.data();
    }
    
    // Update user document
    if (userDoc.exists()) {
      const userPlugins = userData.userPlugins || {};
      userPlugins[pluginId] = buildUserPluginEntry(userPlugins[pluginId], status, requestExtras);
      await updateDoc(userDocRef, {
        userPlugins,
        lastUpdated: serverTimestamp()
      });
    } else {
      const newEntry = buildUserPluginEntry({ customizations: {} }, status, requestExtras);
      await setDoc(userDocRef, {
        uid: userId,
        userPlugins: { [pluginId]: newEntry },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }
    // Also persist status on the account when accountId is provided so the plugin appears shared for the company
    if (accountId) {
      try {
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);
        const accountData = accountDoc.exists() ? accountDoc.data() : {};
        const accountPlugins = accountData.userPlugins || {};
        accountPlugins[pluginId] = buildUserPluginEntry(accountPlugins[pluginId], status, requestExtras);
        if (accountDoc.exists()) {
          await updateDoc(accountRef, {
            userPlugins: accountPlugins,
            lastUpdated: serverTimestamp()
          });
        }
      } catch (accountError) {
        console.error('Error saving plugin status to account:', accountError);
        // Don't fail main operation if account update fails
      }
    }

    // Create/update record in plugins collection for fulfillment tracking
    if (status === 'requested') {
      const keyFromUser =
        requestExtras && String(requestExtras.apiKey || '').trim()
          ? String(requestExtras.apiKey).trim()
          : null;
      // Check if request already exists
      const existingRequest = await getDoc(doc(db, 'pluginRequests', `${userId}_${pluginId}`));
      
      if (!existingRequest.exists()) {
        // Create new request
        const pluginRequestData = {
          userId,
          pluginId,
          company,
          pluginName: pluginData.title || pluginData.name || 'Untitled Plugin',
          pluginDescription: pluginData.description || '',
          status: 'requested',
          requestedAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          requestedBy: userData.displayName || 
                       (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                       userData.email || 
                       'Unknown User',
          ...(keyFromUser ? { requestApiKey: keyFromUser } : {})
        };
        
        await setDoc(doc(db, 'pluginRequests', `${userId}_${pluginId}`), pluginRequestData);
        
        // Trigger technology fulfillment notification for plugin request
        try {
          const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
          await triggerFulfillmentNotification('technology', {
            type: 'plugin',
            name: pluginRequestData.pluginName,
            description: pluginRequestData.pluginDescription,
            requestedBy: pluginRequestData.requestedBy,
            company: pluginRequestData.company,
            pluginId: pluginId,
            timestamp: new Date().toISOString()
          });
          console.log('✅ Technology fulfillment notification sent for plugin request');
        } catch (notificationError) {
          console.error('❌ Error sending technology fulfillment notification:', notificationError);
          // Don't fail the plugin request if notification fails
        }
      } else {
        // Update existing request
        const updatePayload = {
          status: 'requested',
          lastUpdated: serverTimestamp()
        };
        if (keyFromUser) {
          updatePayload.requestApiKey = keyFromUser;
        }
        await updateDoc(doc(db, 'pluginRequests', `${userId}_${pluginId}`), updatePayload);
      }
    } else if (status === 'off') {
      // Remove request when turned off
      const requestRef = doc(db, 'pluginRequests', `${userId}_${pluginId}`);
      const requestDoc = await getDoc(requestRef);
      
      if (requestDoc.exists()) {
        // Delete the plugin request document completely
        await deleteDoc(requestRef);
      }
    }
    
    return {
      success: true,
      message: 'Plugin status saved successfully'
    };
  } catch (error) {
    console.error('Error saving plugin status:', error);
    throw error;
  }
};

/**
 * Save user's/company's plugin customizations (merged shallowly over existing customizations).
 * When accountId is provided, also persist on accounts/{accountId} so team members share config.
 * @param {string} userId - The user's UID
 * @param {string} pluginId - The plugin ID
 * @param {Object} customizations - Fields to merge into userPlugins[pluginId].customizations
 * @param {string|null} [accountId] - Optional account/company ID for shared storage
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserPluginCustomizations = async (userId, pluginId, customizations, accountId = null) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    const mergePlugins = (prevPlugins) => {
      const userPlugins = { ...(prevPlugins || {}) };
      const prevEntry = userPlugins[pluginId] || {};
      const prevCustom = prevEntry.customizations || {};
      userPlugins[pluginId] = {
        ...prevEntry,
        customizations: {
          ...prevCustom,
          ...customizations
        },
        lastUpdated: serverTimestamp()
      };
      return userPlugins;
    };

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const userPlugins = mergePlugins(userData.userPlugins);

      await updateDoc(userDocRef, {
        userPlugins,
        lastUpdated: serverTimestamp()
      });
    } else {
      await setDoc(userDocRef, {
        uid: userId,
        userPlugins: mergePlugins({}),
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }

    if (accountId) {
      try {
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);
        if (accountDoc.exists()) {
          const accountData = accountDoc.data();
          const accountPlugins = mergePlugins(accountData.userPlugins);
          await updateDoc(accountRef, {
            userPlugins: accountPlugins,
            lastUpdated: serverTimestamp()
          });
        }
      } catch (e) {
        console.error('Error saving plugin customizations to account:', e);
      }
    }

    return {
      success: true,
      message: 'Plugin customizations saved successfully'
    };
  } catch (error) {
    console.error('Error saving plugin customizations:', error);
    throw error;
  }
};

/**
 * Update plugin status from CRM side (approve/deny requests)
 * @param {string} pluginId - The plugin ID in the pluginRequests collection
 * @param {string} newStatus - The new status ('approved', 'denied', 'in-progress', 'active')
 * @returns {Promise<Object>} Result of the update operation
 */
export const updatePluginStatusFromCRM = async (pluginId, newStatus) => {
  try {
    // Get the plugin request document
    const pluginDoc = await getDoc(doc(db, 'pluginRequests', pluginId));
    
    if (!pluginDoc.exists()) {
      throw new Error('Plugin request not found');
    }
    
    const pluginData = pluginDoc.data();
    const { userId, pluginId: templateId } = pluginData;
    
    if (!userId || !templateId) {
      throw new Error('Invalid plugin request data');
    }

    /** Mirrors portal saves so Website Calculators sees Active/in-progress when data lives under accounts/{companyId}. */
    const accountId = await getCompanyIdForUser(userId);
    
    // Update the plugin request status
    await updateDoc(doc(db, 'pluginRequests', pluginId), {
      status: newStatus,
      lastUpdated: serverTimestamp()
    });
    
    // Update the user's plugin status
    if (newStatus === 'approved') {
      // When approved, set user status to 'on'
      await saveUserPluginStatus(userId, templateId, 'on', accountId || null, null);
    } else if (newStatus === 'denied') {
      // When denied, set user status to 'off'
      await saveUserPluginStatus(userId, templateId, 'off', accountId || null, null);
    } else if (newStatus === 'in-progress') {
      // When in progress, set user status to 'in-progress'
      await saveUserPluginStatus(userId, templateId, 'in-progress', accountId || null, null);
    } else if (newStatus === 'active') {
      // When active, set user status to 'on'
      await saveUserPluginStatus(userId, templateId, 'on', accountId || null, null);
    }
    
    return {
      success: true,
      message: 'Plugin status updated successfully'
    };
  } catch (error) {
    console.error('Error updating plugin status from CRM:', error);
    throw error;
  }
};
